import { useCallback, useEffect, useRef, useState } from 'react';

import { asApiError, type PSErrorClass } from '@/src/api/apiError';
import eventsApi, { type HomePagePayload } from '@/src/api/eventsApi';

/**
 * useHomePageEvents — the `GET /home` fetch behind `app/index`.
 *
 * Home renders its sections from ONE call, so this is one hook returning one
 * payload with every list in it, not a hook per carousel: the sections load,
 * succeed and fail together, which is exactly how the screen has to draw them.
 * There's no id — home is "the current user's home" — so the fetch runs once on
 * mount and there's no per-id staleness to track.
 *
 * ── What it returns, and why `error` is an object and not a boolean ──────
 *
 *   data       the payload, or null until it has arrived / if it failed
 *   isLoading  a request is in flight (true on mount, and again on `retry`)
 *   error      the failure, or null
 *   retry      re-run the fetch; back to `isLoading` while it's in the air
 *   refresh    re-run the fetch keeping the current page drawn; awaitable
 *
 * `retry` and `refresh` are the same request wearing two different waits, which
 * is why both exist. `retry` backs an error section's button, so it clears to
 * `isLoading` and the page redraws its bones. `refresh` backs pull-to-refresh,
 * where the RefreshControl IS the spinner: dropping to skeletons under it would
 * throw away a page the user is still looking at, so `data` stays put until the
 * new payload lands. It also RESOLVES, because the control needs to be told when
 * to stop — which is the whole reason one returns `Promise<void>` and the other
 * returns nothing.
 *
 * The screen does NOT draw one "something went wrong" for every failure — it
 * draws a different thing per KIND (connection copy with a refresh for
 * 'network', a toast for 'server' and 'api', generic copy for 'unknown'). That
 * decision belongs to the view, but it can only be made from something that
 * survives the trip out of the data layer, so `error` is the whole `ApiError`:
 * `kind` says which treatment, `message` is the text to show when the kind is
 * one whose message is user-facing ('api' — see apiError's docstring for which
 * are and which aren't).
 *
 * Two things follow from that, both deliberate:
 *
 *   - The hook NEVER toasts. A toast is a view decision (and one this screen
 *     can't even make yet — PSToastHost is mounted only under `app/auth`, see
 *     its docstring), so raising one from here would fire it on a screen with
 *     nowhere to draw it, and fire it again for any second consumer of the hook.
 *   - Every failure is a FRESH `ApiError` instance, so a `useEffect` keyed on
 *     `error` fires once per failed attempt — including when a retry fails the
 *     same way it just did, which a kind/message-keyed effect would swallow.
 *
 * Anything caught goes through `asApiError`, so `error.kind` is always readable
 * even when the throw came from our own mapping rather than the server.
 */

/**
 * The fetch's state, kept as a discriminated union INTERNALLY so the impossible
 * combinations can't be represented (data and error at once, an error while
 * loading). The public shape below is the flat `data`/`isLoading`/`error` the
 * screen reads, derived from this — those three are what a call site branches
 * on, and a union would make it write a `status` check before every one.
 */
type FetchState =
  | { status: 'loading' }
  | { status: 'success'; data: HomePagePayload }
  | { status: 'error'; error: PSErrorClass };

const LOADING: FetchState = { status: 'loading' };

export type HomePageEventsState = {
  /** Non-null iff the last attempt succeeded. */
  data: HomePagePayload | null;
  isLoading: boolean;
  /** Non-null iff the last attempt failed. Branch on `.kind`, show `.message`. */
  error: PSErrorClass | null;
  /** Re-runs the fetch — what the errored section's refresh calls. */
  retry: () => void;
  /** Re-runs the fetch with the page still drawn — what pull-to-refresh awaits. */
  refresh: () => Promise<void>;
};

export function useHomePageEvents(): HomePageEventsState {
  const [state, setState] = useState<FetchState>(LOADING);
  // Bumped by `retry`; the effect keys off it, so a bump re-runs the fetch.
  const [attempt, setAttempt] = useState(0);
  // Cleared by the unmount cleanup below, so neither the mount fetch nor a
  // refresh still in the air writes state after the screen has gone.
  const isMounted = useRef(true);

  const load = useCallback(async () => {
    try {
      const data = await eventsApi.getHomePageEvents();
      if (isMounted.current) setState({ status: 'success', data });
    } catch (error: unknown) {
      if (isMounted.current)
        setState({ status: 'error', error: asApiError(error) });
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    // False positive: `load` only setStates after awaiting the request, so the
    // write lands in a later microtask, not synchronously in this body.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load();

    return () => {
      isMounted.current = false;
    };
  }, [attempt, load]);

  const retry = useCallback(() => {
    // Straight back to loading rather than sitting on the error while the
    // second attempt is in flight — the page redraws its bones, and a retry
    // that fails the same way still reads as a new failure to the view.
    setState(LOADING);
    setAttempt((n) => n + 1);
  }, []);

  return {
    data: state.status === 'success' ? state.data : null,
    isLoading: state.status === 'loading',
    error: state.status === 'error' ? state.error : null,
    retry,
    refresh: load,
  };
}
